import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  FolderKanban, 
  Settings, 
  Layers, 
  Network, 
  ClipboardList,
  Target,
  FileText,
  Plus,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp
} from 'lucide-react';
import { psAPI } from '../../services/api';

const PSDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await psAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching PS dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const configCards = [
    {
      title: 'Project Profiles',
      description: 'Define project behavior, organizational defaults, and control settings',
      icon: Settings,
      path: '/ps/config/project-profiles',
      color: 'text-orange-600'
    },
    {
      title: 'WBS Profiles',
      description: 'Configure WBS element settings, account assignment, and planning options',
      icon: Layers,
      path: '/ps/config/wbs-profiles',
      color: 'text-green-600'
    },
    {
      title: 'Network Profiles',
      description: 'Set up network and activity defaults, scheduling parameters',
      icon: Network,
      path: '/ps/config/network-profiles',
      color: 'text-orange-600'
    },
    {
      title: 'Status Profiles',
      description: 'Define user statuses and transition rules for project objects',
      icon: ClipboardList,
      path: '/ps/config/status-profiles',
      color: 'text-orange-600'
    },
    {
      title: 'Planning Profiles',
      description: 'Configure cost planning methods, versions, and integration settings',
      icon: Target,
      path: '/ps/config/planning-profiles',
      color: 'text-orange-600'
    }
  ];

  const operationalCards = [
    {
      title: 'Project Definitions',
      description: 'Create and manage project master data',
      icon: FolderKanban,
      path: '/ps/projects',
      color: 'text-orange-600',
      action: 'View Projects'
    },
    {
      title: 'WBS Elements',
      description: 'Manage Work Breakdown Structure elements',
      icon: Layers,
      path: '/ps/wbs-elements',
      color: 'text-green-600',
      action: 'View WBS'
    },
    {
      title: 'Project Templates',
      description: 'Reusable project structures for quick project creation',
      icon: FileText,
      path: '/ps/templates',
      color: 'text-orange-600',
      action: 'View Templates'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'CREATED': 'bg-gray-100 text-gray-800',
      'RELEASED': 'bg-orange-100 text-orange-800',
      'PARTIALLY_RELEASED': 'bg-yellow-100 text-yellow-800',
      'TECO': 'bg-green-100 text-green-800',
      'CLOSED': 'bg-slate-100 text-slate-800',
      'LOCKED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="ps-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Systems (PS)</h1>
          <p className="text-gray-500 mt-1">SAP-standard project management module</p>
        </div>
        <Button 
          onClick={() => navigate('/ps/projects/create')} 
          className="bg-orange-600 hover:bg-orange-700"
          data-testid="create-project-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Projects</p>
                <p className="text-2xl font-bold">{dashboard?.total_projects || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <FolderKanban className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Projects</p>
                <p className="text-2xl font-bold text-green-600">{dashboard?.active_projects || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard?.completed_projects || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Delayed</p>
                <p className="text-2xl font-bold text-red-600">{dashboard?.delayed_projects || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {dashboard?.alerts && dashboard.alerts.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Attention Required</p>
                <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                  {dashboard.alerts.map((alert, idx) => (
                    <li key={alert.id || alert.message || `alert-${idx}`}>{alert.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Configuration Section */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {configCards.map((card) => (
            <Card 
              key={card.path}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(card.path)}
              data-testid={`config-card-${card.path.split('/').pop()}`}
            >
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <div className={`h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-3 ${card.color}`}>
                    <card.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-medium text-sm">{card.title}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{card.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Operational Section */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FolderKanban className="h-5 w-5" />
          Project Management
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {operationalCards.map((card) => (
            <Card 
              key={card.path}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(card.path)}
              data-testid={`operational-card-${card.path.split('/').pop()}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center ${card.color}`}>
                    <card.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{card.title}</CardTitle>
                    <CardDescription className="text-xs">{card.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Button variant="link" className="p-0 h-auto text-orange-600">
                  {card.action} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Status Distribution */}
      {dashboard?.projects_by_status && Object.keys(dashboard.projects_by_status).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Projects by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dashboard.projects_by_status).map(([status, count]) => (
                  <Badge 
                    key={status} 
                    variant="secondary"
                    className={getStatusColor(status)}
                  >
                    {status}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Projects by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dashboard.projects_by_type || {}).map(([type, count]) => (
                  <Badge key={type} variant="outline">
                    {type}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Projects */}
      {dashboard?.recent_projects && dashboard.recent_projects.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Projects</CardTitle>
              <Button variant="link" onClick={() => navigate('/ps/projects')}>
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {dashboard.recent_projects.map((project) => (
                <div 
                  key={project.project_id} 
                  className="py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 -mx-6 px-6"
                  onClick={() => navigate(`/ps/projects/${project.project_id}`)}
                >
                  <div className="flex items-center gap-3">
                    <FolderKanban className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-sm">{project.name}</p>
                      <p className="text-xs text-gray-500">{project.project_id} • {project.company_code_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className={getStatusColor(project.system_status)}>
                      {project.system_status}
                    </Badge>
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {new Date(project.start_date).toLocaleDateString()} - {new Date(project.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </Layout>
  );
};

export default PSDashboard;
